import './UserSubscription.css';
import './SubscriptionCheckout.css';
import '../tasks/tasklist.css';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import CorporateSubscription from '../user subscriptions/coporatesubscription';
import { Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import axiosInstance from '../../services/AxiosInstance';
import { toast } from 'react-toastify';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { clearSubscriptionCache as clearFeatureCache } from '../../utils/featureLock';
import { getActiveSubscriptions, clearSubscriptionCache } from '../../utils/subscriptionCache';
import { sortSubscriptionPlansBySortOrder } from '../../utils/subscriptionPlansSort';
import { formatFlowdoPlanWithModule } from '../../utils/subscriptionPlanLabels';
import { normalizeModuleTabKey, subscriptionPlansApiUrl } from '../../utils/subscriptionModuleTabs';

function getPlanPriceForCycle(plan, cycle) {
  return parseFloat(cycle === 'yearly' ? plan?.yearly_price ?? 0 : plan?.monthly_price ?? 0) || 0;
}

function getTotalDueTodayDisplay({
  pricingPreviewLoading,
  selectedPlan,
  billingCycle,
  pricingPreview,
  pricingPreviewError,
  checkoutIntentForPreview,
}) {
  if (pricingPreviewLoading) return '…';
  const listPrice = getPlanPriceForCycle(selectedPlan, billingCycle);
  const amt =
    pricingPreview != null
      ? Number(pricingPreview.amount)
      : pricingPreviewError &&
          ['upgrade', 'plan_change', 'downgrade'].includes(checkoutIntentForPreview)
        ? 0
        : listPrice;
  return <span className="text-nowrap">{`$${amt.toFixed(2)}`}</span>;
}

/** Same rules as checkout navigation so preview matches payment. */
function getCheckoutIntent(currentPlan, selectedPlan, billingCycle) {
  if (!selectedPlan) return 'new';
  if (!currentPlan) return 'new';
  if (selectedPlan.id === currentPlan.id) return 'renewal';
  const oldP = getPlanPriceForCycle(currentPlan, billingCycle);
  const newP = getPlanPriceForCycle(selectedPlan, billingCycle);
  if (newP > oldP) return 'upgrade';
  if (newP < oldP) return 'downgrade';
  return 'plan_change';
}

/**
 * Current subscription plan for the same product module as the selected plan.
 * Avoids using only subscriptions[0], which breaks upgrade summary when multiple modules are subscribed.
 */
function resolveActivePlanForSelectedPlan(selectedPlan, subscriptionPlans, subscriptions) {
  if (!selectedPlan || !subscriptionPlans?.length || !subscriptions?.length) return null;
  const mid = selectedPlan.module_id ?? selectedPlan.Module?.id;
  if (mid == null) return null;
  const sub = subscriptions.find((s) => {
    const sp = s.SubscriptionPlan;
    const pid = sp?.id ?? s.subscription_plan_id;
    const planRow = subscriptionPlans.find((pl) => pl.id === pid);
    const moduleId = planRow?.module_id ?? sp?.module_id;
    return moduleId === mid;
  });
  if (!sub) return null;
  const planId = sub.subscription_plan_id ?? sub.SubscriptionPlan?.id;
  return subscriptionPlans.find((p) => p.id === planId) || null;
}

const UserSubscription = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [userSubscription, setUserSubscription] = useState(null);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const confirmSectionRef = useRef(null);
  const [pricingPreview, setPricingPreview] = useState(null);
  const [pricingPreviewLoading, setPricingPreviewLoading] = useState(false);
  const [pricingPreviewError, setPricingPreviewError] = useState(null);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axiosInstance.get(subscriptionPlansApiUrl());
      if(response.data){
        const plans = sortSubscriptionPlansBySortOrder([...response.data.data]);
        setSubscriptionPlans(plans);
        return plans;
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    } 
  };

  // Parse URL parameters and select plan
  useEffect(() => {
    const handlePlanSelection = async () => {
      const urlParams = new URLSearchParams(location.search);
      const planId = urlParams.get('plan');

      if (planId) {
        const planIdNumber = parseInt(planId);
        const subscriptionPlan = await fetchSubscriptionPlans();
        const planToSelect = subscriptionPlan.find(plan => plan.id == planIdNumber);

        if (planToSelect) {
          setSelectedPlan(planToSelect);
          setActiveTab('manage'); // Switch to manage tab to show the selected plan
        }
      }
    };

    handlePlanSelection();
  }, [location.search]);

  // Fetch user's active subscription
  useEffect(() => {
    const fetchActiveSubscription = async () => {
      try {
        // Use shared cache utility
        const response = await getActiveSubscriptions();

        if (response && response.subscriptions && response.subscriptions.length > 0) {
          setActiveSubscriptions(response.subscriptions);
          const activeSub = response.subscriptions[0];
          setUserSubscription({
            id: activeSub.id,
            SubscriptionPlanId: activeSub.subscription_plan_id
          });
        } else {
          setActiveSubscriptions([]);
        }
      } catch (error) {
        console.error('Error fetching active subscription:', error);
        // Don't show error toast as this is expected for new users
      }
    };

    fetchActiveSubscription();
  }, []);

  const activePlanForSelection = useMemo(() => {
    if (!selectedPlan) return null;
    return resolveActivePlanForSelectedPlan(selectedPlan, subscriptionPlans, activeSubscriptions);
  }, [selectedPlan, subscriptionPlans, activeSubscriptions]);

  const checkoutIntentForPreview = useMemo(() => {
    return getCheckoutIntent(activePlanForSelection, selectedPlan, billingCycle);
  }, [activePlanForSelection, selectedPlan, billingCycle]);

  const showConfirmSection =
    selectedPlan &&
    (!activePlanForSelection || selectedPlan.id !== activePlanForSelection.id);

  const shouldFetchPricingPreview =
    showConfirmSection &&
    selectedPlan &&
    !(
      parseFloat(selectedPlan.monthly_price || 0) === 0 &&
      parseFloat(selectedPlan.yearly_price || 0) === 0
    );

  useEffect(() => {
    if (!shouldFetchPricingPreview) {
      setPricingPreview(null);
      setPricingPreviewError(null);
      setPricingPreviewLoading(false);
      return;
    }
    const planId = selectedPlan.id || selectedPlan.planId;
    let cancelled = false;
    (async () => {
      setPricingPreviewLoading(true);
      setPricingPreviewError(null);
      try {
        const res = await axiosInstance.get('/payment/preview-checkout', {
          params: {
            subscription_plan_id: planId,
            billing_cycle: billingCycle,
            checkout_intent: checkoutIntentForPreview,
            user_count: 1,
          },
        });
        if (cancelled) return;
        if (res.data?.success) {
          setPricingPreview(res.data);
          setPricingPreviewError(null);
        } else {
          setPricingPreview(null);
          setPricingPreviewError(res.data?.message || 'Failed to load pricing');
        }
      } catch (e) {
        if (cancelled) return;
        setPricingPreview(null);
        if (['upgrade', 'plan_change', 'downgrade'].includes(checkoutIntentForPreview)) {
          setPricingPreviewError(
            e.response?.data?.message || 'Could not load prorated pricing. Please try again.'
          );
        } else {
          setPricingPreviewError(null);
        }
      } finally {
        if (!cancelled) setPricingPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    shouldFetchPricingPreview,
    selectedPlan?.id,
    selectedPlan?.planId,
    billingCycle,
    checkoutIntentForPreview,
  ]);

  useEffect(() => {
    if (!selectedPlan) return;
    if (activePlanForSelection && selectedPlan.id === activePlanForSelection.id) return;
    const t = setTimeout(() => {
      confirmSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [selectedPlan, activePlanForSelection]);

  const isFreePlan = () => {
    return selectedPlan &&
      parseFloat(selectedPlan.monthly_price || 0) === 0 &&
      parseFloat(selectedPlan.yearly_price || 0) === 0;
  };

  const isConfirmButtonEnabled = () => isFreePlan();

  // Navigate to Shopping Cart (Checkout) for paid plans
  const handleProceedToCheckout = async () => {
    if (!selectedPlan) {
      toast.error('Please select a subscription plan');
      return;
    }
    if (isFreePlan()) {
      toast.error('This plan is free. Use Confirm Subscription instead.');
      return;
    }
    // Re-fetch active subscription so checkoutIntent is correct even if initial load was slow
    let planForComparison = activePlanForSelection;
    try {
      const fresh = await getActiveSubscriptions(true);
      const subs = fresh?.subscriptions || [];
      if (subs.length > 0 && subscriptionPlans.length > 0) {
        setActiveSubscriptions(subs);
        planForComparison =
          resolveActivePlanForSelectedPlan(selectedPlan, subscriptionPlans, subs) || planForComparison;
      }
    } catch (e) {
      console.warn('Proceed to checkout: could not refresh subscriptions', e);
    }

    const checkoutIntent = getCheckoutIntent(planForComparison, selectedPlan, billingCycle);
    navigate(
      `/subscription/checkout?planId=${selectedPlan.id || selectedPlan.planId}&billingCycle=${billingCycle}&checkoutIntent=${checkoutIntent}`
    );
  };

  // Handle subscription (free plans only)
  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.error('Please select a subscription plan');
      return;
    }

    if (!isConfirmButtonEnabled()) {
      toast.error('This plan is not available for free subscription');
      return;
    }

    setIsSubscribing(true);
    try {
      const response = await axiosInstance.post('/userSubscription/subscribe', {
        subscription_plan_id: selectedPlan.planId || selectedPlan.id
      });

      if (response) {
        toast.success('Successfully subscribed to the plan!');
        // Clear cache and refresh active subscription data
        clearSubscriptionCache();
        clearFeatureCache();
        const activeResponse = await getActiveSubscriptions(true); // Force refresh
        if (activeResponse && activeResponse.subscriptions && activeResponse.subscriptions.length > 0) {
          setActiveSubscriptions(activeResponse.subscriptions);
          const activeSub = activeResponse.subscriptions[0];
          setUserSubscription({
            id: activeSub.id,
            SubscriptionPlanId: activeSub.subscription_plan_id
          });
        }
        // Refresh features in localStorage and notify app
        try {
          const featuresResponse = await axiosInstance.get('/userSubscription/features');
          if (featuresResponse.data && featuresResponse.data.features) {
            localStorage.setItem('userPlanFeatures', JSON.stringify(featuresResponse.data.features));
          }
        } catch (err) {
          console.error('Error refreshing user features after subscription:', err);
        }
        try { window.dispatchEvent(new Event('user-subscription-updated')); } catch (e) {}
        setSelectedPlan(null);
      } else {
        toast.error(response.data.message || 'Failed to subscribe');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  // Get initial module tab from URL (e.g. ?module=document) when coming from subscription-feature-required
  const urlParams = new URLSearchParams(location.search);
  const moduleParam = urlParams.get('module');
  const initialModuleTab = moduleParam ? normalizeModuleTabKey(moduleParam) : null;

  const renderManageTab = () => (
    <div className="manage-container">
      <CorporateSubscription
        key={initialModuleTab || 'default'}
        activePlanId={userSubscription?.SubscriptionPlanId}
        parentSelectedPlan={selectedPlan}
        onPlanSelect={(plan) => setSelectedPlan({...plan})}
        onBillingCycleChange={setBillingCycle}
        onModuleTabChange={() => setSelectedPlan(null)}
        initialModuleTab={initialModuleTab}
      />

      {/* Confirm / Pay Button Section */}
      {selectedPlan &&
        (!activePlanForSelection || selectedPlan.id !== activePlanForSelection.id) && (
        <div ref={confirmSectionRef} className="confirm-section mt-4">
          <div className="w-100">
            {!isFreePlan() && (
              <>
                {pricingPreviewError &&
                  ['upgrade', 'plan_change', 'downgrade'].includes(checkoutIntentForPreview) && (
                    <Alert variant="warning" className="checkout-alert mb-3 text-start w-100">
                      {pricingPreviewError}
                    </Alert>
                  )}
                <div className="subscription-confirm-pricing subscription-confirm-detail-layout text-start mb-4 w-100">
                  {pricingPreviewLoading ? (
                    <Row className="g-3 subscription-confirm-pricing-cols">
                      {checkoutIntentForPreview !== 'upgrade' && selectedPlan && (
                        <Col xs={12} md={6} className="subscription-confirm-col-left">
                          <div className="checkout-detail-rows">
                            <div className="checkout-detail-row">
                              <span>Selected plan:</span>
                              <span className="fw-semibold checkout-detail-value">
                                {formatFlowdoPlanWithModule(selectedPlan)}
                              </span>
                            </div>
                          </div>
                        </Col>
                      )}
                      <Col
                        xs={12}
                        md={checkoutIntentForPreview !== 'upgrade' && selectedPlan ? 6 : 12}
                        className={checkoutIntentForPreview !== 'upgrade' && selectedPlan ? 'subscription-confirm-col-right' : undefined}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2 py-3">
                          <Spinner animation="border" size="sm" style={{ color: '#C42126' }} />
                          <span className="text-muted">Loading pricing…</span>
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Row className="g-3 subscription-confirm-pricing-cols">
                        {pricingPreview?.proration ? (
                          <>
                            <Col xs={12} md={6} className="subscription-confirm-col-left">
                              <div className="checkout-detail-rows">
                                {checkoutIntentForPreview !== 'upgrade' && selectedPlan && (
                                  <div className="checkout-detail-row">
                                    <span>Selected plan:</span>
                                    <span className="fw-semibold checkout-detail-value">
                                      {formatFlowdoPlanWithModule(selectedPlan)}
                                    </span>
                                  </div>
                                )}
                                <div className="checkout-detail-row">
                                  <span>Period:</span>
                                  <span className="fw-semibold checkout-detail-value">
                                    {pricingPreview?.period_label ||
                                      (billingCycle === 'yearly' ? '1 year' : '1 month')}
                                  </span>
                                </div>
                                <div className="checkout-detail-row">
                                  <span>Plan amount:</span>
                                  <span className="fw-semibold checkout-detail-value">
                                    {`$${Number(pricingPreview.proration.new_full_price).toFixed(2)}`}
                                  </span>
                                </div>
                                <div className="checkout-detail-row">
                                  <span>Credit from current plan:</span>
                                  <span className="fw-semibold checkout-detail-value">
                                    {`−$${Number(pricingPreview.proration.old_credit).toFixed(2)}`}
                                  </span>
                                </div>
                              </div>
                            </Col>
                            <Col xs={12} md={6} className="subscription-confirm-col-right">
                              <div className="checkout-detail-rows">
                                <div className="checkout-detail-row">
                                  <span>Days remaining (current period):</span>
                                  <span className="fw-semibold checkout-detail-value">
                                    {Math.round(Number(pricingPreview.proration.remaining_days))}
                                  </span>
                                </div>
                                <div className="checkout-detail-row">
                                  <span>New plan (prorated):</span>
                                  <span className="fw-semibold checkout-detail-value">
                                    {`$${Number(pricingPreview.proration.new_prorated).toFixed(2)}`}
                                  </span>
                                </div>
                              </div>
                            </Col>
                          </>
                        ) : (
                          <>
                            <Col xs={12} md={6} className="subscription-confirm-col-left">
                              <div className="checkout-detail-rows">
                                {checkoutIntentForPreview !== 'upgrade' && selectedPlan && (
                                  <div className="checkout-detail-row">
                                    <span>Selected plan:</span>
                                    <span className="fw-semibold checkout-detail-value">
                                      {formatFlowdoPlanWithModule(selectedPlan)}
                                    </span>
                                  </div>
                                )}
                                <div className="checkout-detail-row">
                                  <span>Period:</span>
                                  <span className="fw-semibold checkout-detail-value">
                                    {pricingPreview?.period_label ||
                                      (billingCycle === 'yearly' ? '1 year' : '1 month')}
                                  </span>
                                </div>
                              </div>
                            </Col>
                            <Col xs={12} md={6} className="subscription-confirm-col-right">
                              <div className="checkout-detail-rows">
                                <div className="checkout-detail-row">
                                  <span>Plan amount:</span>
                                  <span className="fw-semibold checkout-detail-value">
                                    {`$${getPlanPriceForCycle(selectedPlan, billingCycle).toFixed(2)}`}
                                  </span>
                                </div>
                              </div>
                            </Col>
                          </>
                        )}
                      </Row>
                      <div className="subscription-confirm-total-block w-100">
                        <Row className="g-3 subscription-confirm-total-row">
                          <Col xs={12} md={6} className="d-none d-md-block" aria-hidden="true" />
                          <Col xs={12} md={6}>
                            <div className="checkout-detail-row subscription-confirm-total-inner">
                              <span className="fw-bold">Total due today:</span>
                              <span className="fw-bold checkout-detail-value">
                                {getTotalDueTodayDisplay({
                                  pricingPreviewLoading,
                                  selectedPlan,
                                  billingCycle,
                                  pricingPreview,
                                  pricingPreviewError,
                                  checkoutIntentForPreview,
                                })}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            {isFreePlan() && (
              <div className="subscription-confirm-pricing subscription-confirm-detail-layout text-start mb-4 w-100">
                <Row className="g-3 subscription-confirm-pricing-cols">
                  <Col xs={12} md={6} className="subscription-confirm-col-left">
                    <div className="checkout-detail-rows">
                      <div className="checkout-detail-row">
                        <span>Selected plan:</span>
                        <span className="fw-semibold checkout-detail-value">
                          {formatFlowdoPlanWithModule(selectedPlan)}
                        </span>
                      </div>
                      <div className="checkout-detail-row">
                        <span>Period:</span>
                        <span className="fw-semibold checkout-detail-value">
                          {billingCycle === 'yearly' ? '1 year' : '1 month'}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={6} className="subscription-confirm-col-right">
                    <div className="checkout-detail-rows">
                      <div className="checkout-detail-row">
                        <span>Plan amount:</span>
                        <span className="fw-semibold checkout-detail-value">
                          {`$${getPlanPriceForCycle(selectedPlan, billingCycle).toFixed(2)}`}
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
                <div className="subscription-confirm-total-block w-100">
                  <Row className="g-3 subscription-confirm-total-row">
                    <Col xs={12} md={6} className="d-none d-md-block" aria-hidden="true" />
                    <Col xs={12} md={6}>
                      <div className="checkout-detail-row subscription-confirm-total-inner">
                        <span className="fw-bold">Total due today:</span>
                        <span className="fw-bold checkout-detail-value">
                          {`$${getPlanPriceForCycle(selectedPlan, billingCycle).toFixed(2)}`}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
            <div className="text-center">
            {isFreePlan() ? (
              <>
                <Button
                  variant="danger"
                  size="lg"
                  onClick={handleSubscribe}
                  disabled={!isConfirmButtonEnabled() || isSubscribing}
                  className="px-5 py-3 confirm-button"
                >
                  {isSubscribing ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-2"></i>
                      Confirm Free Subscription
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="danger"
                  size="lg"
                  onClick={handleProceedToCheckout}
                  className="px-5 py-3 confirm-button"
                  disabled={
                    ['upgrade', 'plan_change', 'downgrade'].includes(checkoutIntentForPreview) &&
                    pricingPreviewLoading
                  }
                >
                  <i className="fas fa-shopping-cart me-2"></i>
                  Proceed to Checkout
                </Button>
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="subscription-page">
        <div className="row">
          <div className="col-xl-12">
            <div className="row">
              <div className="col-xl-12">
                <div className="row">
                  <div className="col-xl-12">
                    <div className="card border-0 shadow-sm main-card">

                      <div className="card-body p-0">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-4 pt-4 pb-2 border-bottom">
                          <div>
                            <h5 className="mb-1">Subscriptions</h5>
                            <p className="text-muted small mb-0">Manage plans and billing per module</p>
                          </div>
                          <Link to="/credits" className="new-user-btn">
                            <i className="fas fa-wallet me-2" aria-hidden="true" />
                            Credit wallet
                          </Link>
                        </div>
                        {renderManageTab()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      <InternalFooter />
    </div>
  );
};

export default UserSubscription; 